import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { 
  Box,
  Grid,
  MenuItem, 
  Skeleton, 
  TextField, 
  Typography 
} from '@mui/material';

import TopBar from '../../../components/utils/TopBar';
import NA from '../../../components/utils/NA';

import "./styles.scss";

const EnvironmentsList = [
  { label: "ALPHA", value: "alpha" },
  { label: "PROD", value: "prod" }
];

const FE_APPS = [
  { label: "fe-core-web",  alphaUrl: "https://sandbox-app.lusid.ai",     prodUrl: "https://app.lusidpay.com" },
  { label: "fe-lusid-web", alphaUrl: "https://sandbox.lusid.ai",          prodUrl: "https://lusidpay.com" },
  { label: "fe-internal",  alphaUrl: "https://sandbox-internal.lusid.ai", prodUrl: "https://internal.lusidpay.com" },
  { label: "fe-altcard-internal", alphaUrl: "https://sandbox-internal.altcardnetwork.com",prodUrl: "https://internal.altcardnetwork.com" },
  { label: "fe-developer", alphaUrl: "https://sandbox-developer.lusid.ai",prodUrl: "https://developer.lusidpay.com" },
];

const BE_APPS = [
  { label: "be-core",           alphaUrl: "https://alpha-api.lusid.ai/us/core/app-details",         prodUrl: "https://api.lusidpay.com/us/core/app-details" },
  { label: "be-identity",       alphaUrl: "https://alpha-api.lusid.ai/us/identity/app-details",     prodUrl: "https://api.lusidpay.com/us/identity/app-details" },
  { label: "be-fusion",         alphaUrl: "https://alpha-api.lusid.ai/us/fusion/app-details",       prodUrl: "https://api.lusidpay.com/us/fusion/app-details" },
  { label: "be-webhook",        alphaUrl: "https://alpha-webhook.lusid.ai/app-details",             prodUrl: "https://webhook.lusidpay.com/app-details" },
  { label: "be-altcard",        alphaUrl: "https://alpha-api.altcardnetwork.com/app-details",       prodUrl: "https://api.altcardnetwork.com/app-details" },
  { label: "be-accounting-tpi", alphaUrl: "https://alpha-api.lusid.ai/us/accounting-tpi/app-details", prodUrl: "https://api.lusidpay.com/us/accounting-tpi/app-details" },
];

const AppVersionsPage = () => {
  const [selectedEnvironment, setSelectedEnvironment] = useState("alpha");
  const [loadingFE, setLoadingFE] = useState(Array(FE_APPS.length).fill(true));
  const [loadingBE, setLoadingBE] = useState(true);

  const [appVersionsBE, setAppVersionsBE] = useState(Array(BE_APPS.length).fill(null));

  useEffect(() => {
    setLoadingFE(Array(FE_APPS.length).fill(true));
    handleGetVersionBE();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnvironment]); 
  
  async function handleGetVersionBE(){
    setLoadingBE(true);
    const results = Array(BE_APPS.length).fill(null);
    setAppVersionsBE([...results]);
    try {
      if (selectedEnvironment) {
        await Promise.all(BE_APPS.map(async (app, index) => {
          const url = selectedEnvironment === "alpha" ? app.alphaUrl : app.prodUrl;
          if (!url) return;
          try {
            const response = await axios.get(url);
            results[index] = response.data;
          } catch {
            results[index] = { version: "Error fetching version" };
          }
        }));
      }
    } finally {
      setAppVersionsBE([...results]);
      setLoadingBE(false);
    }
  }

  const handleChangeFEloading = (index) => {
    setLoadingFE(prev => {
      const temp = [...prev];
      temp[index] = false;
      return temp;
    });
  };

  const LabelRow = ({ label, value }) =>
    value ? (
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'baseline', width: '100%' }}>
        <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontWeight: 600 }}>
          {label}:
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          {value}
        </Typography>
      </Box>
    ) : null;

  const VersionCard = ({ data }) => {
    const [expanded, setExpanded] = useState(false);
    return (
      <Box
        sx={{
          width: "100%",
          color: "black",
          backgroundColor: "white",
          borderRadius: "5px",
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          boxSizing: 'border-box',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(prev => !prev)}
        title={expanded ? "Click to collapse" : "Click to see full JSON"}
      >
        <LabelRow label="Version"  value={data?.version ?? '-'} />
        <LabelRow label="App"      value={data?.app} />
        <LabelRow label="Desc"     value={data?.description} />
        <LabelRow label="Env"      value={data?.app_environment} />
        <LabelRow label="Node"     value={data?.node_environment} />
        {expanded && (
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              p: 1,
              backgroundColor: "#f4f4f4",
              borderRadius: "4px",
              fontSize: "0.65rem",
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              color: "#333",
            }}>
            {JSON.stringify(data, null, 2)}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box width={"100%"}>

      <TopBar 
        title={"App Versions"}
        />

      <Box
        className="flexCenter_Row"
        gap={"20px"}
        mb={"15px"}
        sx={{ width: "350px" }}>
        <Typography>
          Environment
        </Typography>
        <TextField
          select
          size='small'
          label="Environment"
          fullWidth
          value={selectedEnvironment || ""}
          onChange={(e) => {
            setSelectedEnvironment(e.target.value);
          }}
          inputProps={{ maxLength: 32 }}
          >
          {
            EnvironmentsList.map((item, index) => (
              <MenuItem
                key={index}
                value={item.value}>
                {item.label || <NA />}
              </MenuItem>
            ))
          }
        </TextField>
      </Box>

      <Box
        sx={{ 
          height: "calc(100vh - 200px)",
        }}>
        <Box  
          height={"50%"}>
          <Typography
            mb={"20px"}
            variant='subtitle2'>
            Frontend
          </Typography>
          <Grid container spacing={2}>
            {FE_APPS.map((app, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  className="flexCenterCenterColumn app_card"
                  gap={"10px"}>
                  <Typography>{app.label}</Typography>
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      display: loadingFE[index] ? 'flex' : 'none',
                      width: "100%",
                      height: '100px',
                      borderRadius: "5px",
                      backgroundColor: "#a1a1a1",
                    }}
                  />
                  <iframe
                    src={`${selectedEnvironment === "alpha" ? app.alphaUrl : app.prodUrl}/version`}
                    title={app.label}
                    onLoad={() => handleChangeFEloading(index)}
                    style={{
                      display: loadingFE[index] ? 'none' : 'block',
                      width: '100%',
                      height: '100px',
                      border: 'none',
                      backgroundColor: "white",
                      borderRadius: "5px",
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box
          height={"50%"}>
          <Typography
            mb={"20px"}
            variant='subtitle2'>
            Backend
          </Typography>
          <Grid container spacing={2}>
            {BE_APPS.map((app, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Box
                  className="flexCenterCenterColumn app_card"
                  gap={"10px"}>
                  <Typography>{app.label}</Typography>
                  {
                    loadingBE
                    ?
                    <Skeleton
                      variant="rectangular"
                      sx={{
                        width: "100%",
                        height: '100px',
                        borderRadius: "5px",
                        backgroundColor: "#a1a1a1",
                      }}
                    />
                    :
                    <VersionCard data={appVersionsBE[index]} />
                  }
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    
    </Box>
  )
}

export default AppVersionsPage;